
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, Popover, Input, Card, Col, Row, Menu, Dropdown, DatePicker } from 'antd';
import { Download, Filter, Search } from 'react-feather';
import Select from 'react-select';
import { countries } from "countries-list";

import SelectCorporate from '../../components/ui/SelectCorporate';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import axios from 'axios';
import Swal from 'sweetalert2';
import exportService from '../../utils/exportService';
import debounce from 'lodash.debounce';
import GuestImportModal from '../../components/popups/GuestImportModal';
import GuestProfileModal from '../../components/popups/GuestProfileModal';

// Sample data for initial rendering
const sampleData = [
  {
    guest_id: 'G001',
    companyid: 'C001',
    full_name: 'John Alexander Doe',
    gender: 'Male',
    date_of_birth: '1985-03-15',
    marriage_anniversary: '2010-06-20',
    nationality: 'USA',
    gst_number: 'GST123456789',
    gst_company_name: 'Doe Enterprises',
    gst_company_address: '123 Business Park, NY',
    passport_number: 'US123456789',
    passport_expiry: '2028-12-31',
    address: '456 Park Avenue, New York',
    city: 'New York',
    state: 'NY',
    country: 'USA',
    zip_code: '10022',
    id_proof_type: 'Passport',
    id_proof_number: 'US123456789',
    id_proof_file: 'Uploads/id_proof_G001.pdf',
    id_proof_issuing_authority: 'US Department of State',
    is_authenticated: true,
    visa_number: 'VISA987654321',
    visa_expiry: '2026-06-30',
    is_regular: true,
    additional_guests: [
      { name: 'Jane Doe', relation: 'Spouse', id_proof: 'US987654321' },
      { name: 'Emma Doe', relation: 'Child', id_proof: 'US111222333' },
    ],
    created_at: '2024-01-10T10:00:00Z',
    updated_at: '2025-06-15T12:00:00Z',
    email: 'john.doe@example.com',
    phone_no: '+1-212-555-1234',
    guest_title: 'Mr.',
    title: 'Mr.',
  },
  {
    guest_id: 'G002',
    companyid: 'C001',
    full_name: 'Priya Sharma',
    gender: 'Female',
    date_of_birth: '1990-07-22',
    marriage_anniversary: null,
    nationality: 'India',
    gst_number: 'GST987654321',
    gst_company_name: 'Sharma Consulting',
    gst_company_address: '456 MG Road, Mumbai',
    passport_number: 'IN456789123',
    passport_expiry: '2027-03-31',
    address: '789 Marine Drive, Mumbai',
    city: 'Mumbai',
    state: 'Maharashtra',
    country: 'India',
    zip_code: '400002',
    id_proof_type: 'Aadhaar',
    id_proof_number: '123456789012',
    id_proof_file: 'Uploads/id_proof_G002.pdf',
    id_proof_issuing_authority: 'UIDAI',
    is_authenticated: true,
    visa_number: null,
    visa_expiry: null,
    is_regular: false,
    additional_guests: [],
    created_at: '2024-03-20T14:30:00Z',
    updated_at: '2025-04-10T09:00:00Z',
    email: 'priya.sharma@example.com',
    phone_no: '+91-9876543210',
    guest_title: 'Ms.',
    title: 'Ms.',
  },
];

// Sample historical stays for testing
const sampleHistoricalStays = [
  {
    booking_id: 'B001',
    guest_id: 'G001',
    check_in_date: '2024-01-15',
    check_out_date: '2024-01-20',
    room_type: 'Deluxe Suite',
    total_amount: '1500.00',
    status: 'Completed',
  },
  {
    booking_id: 'B002',
    guest_id: 'G001',
    check_in_date: '2024-06-10',
    check_out_date: '2024-06-12',
    room_type: 'Executive Room',
    total_amount: '600.00',
    status: 'Completed',
  },
];

const apiURL = process.env.REACT_APP_API_URL || 'https://api.example.com';

const GuestDatabase = () => {
  const { t } = useTranslation();
  const getBusinessDate = () => {
    const storedBusinessDate = localStorage.getItem('business_date');
    return storedBusinessDate ? moment(storedBusinessDate) : moment();
  };
  const [selectedDate, setSelectedDate] = useState(getBusinessDate());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [dateRange, setDateRange] = useState({
    startDate: moment(selectedDate).subtract(30, 'days'),
    endDate: selectedDate,
  });
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [historicalStays, setHistoricalStays] = useState([]);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [metrics, setMetrics] = useState({ totalGuests: 0, regularGuests: 0, recentStays: 0 });
  const [filterVisible, setFilterVisible] = useState(false);

const nationalityOptions = Object.entries(countries)
  .map(([code, country]) => ({
    value: country.name,
    label: country.name,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

  const guestTypeOptions = [
    { value: 'Regular', label: t('regular') },
    { value: 'New', label: t('new') },
    { value: 'VIP', label: t('vip') },
  ];

  const exportHeaders = [
    { key: 'guest_id', label: t('guest_id') },
    { key: 'companyid', label: t('company_id') },
    { key: 'full_name', label: t('full_name') },
    { key: 'guest_title', label: t('guest_title') },
    { key: 'gender', label: t('gender') },
    { key: 'date_of_birth', label: t('date_of_birth') },
    { key: 'marriage_anniversary', label: t('marriage_anniversary') },
    { key: 'nationality', label: t('nationality') },
    { key: 'gst_number', label: t('gst_number') },
    { key: 'gst_company_name', label: t('gst_company_name') },
    { key: 'gst_company_address', label: t('gst_company_address') },
    { key: 'passport_number', label: t('passport_number') },
    { key: 'passport_expiry', label: t('passport_expiry') },
    { key: 'address', label: t('address') },
    { key: 'city', label: t('city') },
    { key: 'state', label: t('state') },
    { key: 'country', label: t('country') },
    { key: 'zip_code', label: t('zip_code') },
    { key: 'id_proof_type', label: t('id_proof_type') },
    { key: 'id_proof_number', label: t('id_proof_number') },
    { key: 'id_proof_file', label: t('id_proof_file') },
    { key: 'id_proof_issuing_authority', label: t('id_proof_issuing_authority') },
    { key: 'is_authenticated', label: t('is_authenticated'), render: (value) => value ? t('yes') : t('no') },
    { key: 'visa_number', label: t('visa_number') },
    { key: 'visa_expiry', label: t('visa_expiry') },
    { key: 'is_regular', label: t('guest_type'), render: (value) => value ? 'Regular' : 'New' },
    { key: 'additional_guests', label: t('additional_guests'), render: (value) => value?.length > 0 ? value.map(ag => `${ag.name} (${ag.relation})`).join(', ') : 'N/A' },
    { key: 'created_at', label: t('created_at') },
    { key: 'updated_at', label: t('updated_at') },
    { key: 'email', label: t('email') },
    { key: 'phone_no', label: t('phone') },
  ];

  const debouncedSearch = useCallback(
    debounce((query) => {
      setFilters((prev) => ({ ...prev, search: query }));
      setPagination((prev) => ({ ...prev, current: 1 }));
    }, 500),
    []
  );

  useEffect(() => {
    fetchMetrics();
    fetchGuests();
  }, [filters, pagination.current, pagination.pageSize, dateRange]);

  const fetchMetrics = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${apiURL}/reports-daily/guest-metrics/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setMetrics(response.data.metrics || { totalGuests: 2, regularGuests: 1, recentStays: 0 });
      }
    } catch (error) {
      setMetrics({ totalGuests: 2, regularGuests: 1, recentStays: 0 });
    }
  };

  const fetchGuests = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${apiURL}/reports-daily/guest-list/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          search: filters.search || undefined,
          nationality: filters.nationality ? filters.nationality.map(n => n.value) : undefined,
          guestType: filters.guestType ? filters.guestType.value : undefined,
          companyid: filters.companyid || undefined,
          startDate: dateRange.startDate.format('YYYY-MM-DD'),
          endDate: dateRange.endDate.format('YYYY-MM-DD'),
          page: pagination.current,
          pageSize: pagination.pageSize,
        },
      });
      if (response.data.success) {
        setData(response.data.guests || sampleData);
        setPagination((prev) => ({ ...prev, total: response.data.pagination.totalRecords || sampleData.length }));
      } else {
        Swal.fire({ title: t('error'), text: t('failed_fetch_guests'), icon: 'error' });
        setData(sampleData);
        setPagination((prev) => ({ ...prev, total: sampleData.length }));
      }
    } catch (error) {
      Swal.fire({ title: t('error'), text: t('failed_fetch_guests'), icon: 'error' });
      setData(sampleData);
      setPagination((prev) => ({ ...prev, total: sampleData.length }));
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoricalStays = async (guestId) => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${apiURL}/reports-daily/guest-bookings/${guestId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setHistoricalStays(response.data.bookings || sampleHistoricalStays);
      } else {
        Swal.fire({ title: t('error'), text: t('failed_fetch_stays'), icon: 'error' });
        setHistoricalStays(sampleHistoricalStays);
      }
    } catch (error) {
      Swal.fire({ title: t('error'), text: t('failed_fetch_stays'), icon: 'error' });
      setHistoricalStays(sampleHistoricalStays);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const handleTableChange = (pagination) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: pagination.total,
    });
  };

  const handleViewProfile = (guest) => {
    setSelectedGuest(guest);
    fetchHistoricalStays(guest.guest_id);
    setProfileModalVisible(true);
  };

  const handleImportSuccess = () => {
    console.log('handleImportSuccess called, closing modal');
    setImportModalVisible(false);
    fetchGuests();
    fetchMetrics();
  };

  const handleCloseImportModal = () => {
    console.log('handleCloseImportModal called, setting importModalVisible to false');
    setImportModalVisible(false);
  };

  const handleCloseProfileModal = () => {
    console.log('handleCloseProfileModal called, setting profileModalVisible to false');
    setProfileModalVisible(false);
    setHistoricalStays([]);
  };

  const filterContent = (
    <div style={{ width: '400px', padding: '10px' }}>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('nationality')}</label>
        <Select
          isMulti
          options={nationalityOptions}
          onChange={(selected) => setFilters({ ...filters, nationality: selected })}
          placeholder={t('select_nationality')}
        />
      </div>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('guest_type')}</label>
        <Select
          options={guestTypeOptions}
          onChange={(selected) => setFilters({ ...filters, guestType: selected })}
          placeholder={t('select_guest_type')}
        />
      </div>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('company')}</label>
        <SelectCorporate
          onSelectCompany={(selected) => setFilters({ ...filters, companyid: selected ? selected.value : null })}
          selectedValue={filters.companyid}
          isDisabled={loading}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('date_range')}</label>
        <DatePicker.RangePicker
          value={[dateRange.startDate, dateRange.endDate]}
          onChange={(dates) => {
            if (dates) {
              setDateRange({ startDate: moment(dates[0]), endDate: moment(dates[1]) });
            }
          }}
          format="DD MMM YYYY"
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );

  const downloadMenu = (
    <Menu>
      <Menu.Item key="xls" onClick={() => exportService.exportToXLS(data, exportHeaders, 'guest_database.xlsx')}>
        {t('export_xls')}
      </Menu.Item>
      <Menu.Item key="csv" onClick={() => exportService.exportToCSV(data, exportHeaders, 'guest_database.csv')}>
        {t('export_csv')}
      </Menu.Item>
      <Menu.Item key="pdf" onClick={() => exportService.exportToPDF(data, exportHeaders, t('guest_database'), 'guest_database.pdf')}>
        {t('export_pdf')}
      </Menu.Item>
    </Menu>
  );

  const metricCards = [
    { label: t('total_guests'), value: metrics.totalGuests, color: '#007bff' },
    { label: t('regular_guests'), value: metrics.regularGuests, color: '#28a745' },
    { label: t('recent_stays'), value: metrics.recentStays, color: '#dc3545' },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="guest-database-container">
          <div className="page-header page-top-icons mb-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <h2 className="mb-0 d-flex align-items-center">
                <i className="fas fa-users text-primary me-2" style={{ fontSize: '22px' }} />
                {t('guest_database')}
              </h2>
              <div className="d-flex align-items-center flex-grow-1 justify-content-center">
                <Input
                  prefix={<Search />}
                  placeholder={t('search_by_name_email_phone')}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  style={{ width: '100%', maxWidth: '600px', height: '40px', fontSize: '16px' }}
                />
              </div>
              <div className="d-flex align-items-center gap-3">
                <Popover
                  content={filterContent}
                  trigger="click"
                  placement="bottomRight"
                  visible={filterVisible}
                  onVisibleChange={setFilterVisible}
                >
                  <Button type="link" style={{ fontSize: '14px', color: '#007bff' }}>
                    <Filter size={16} className="me-1" /> {t('filter')}
                  </Button>
                </Popover>
                <Button
                  type="primary"
                  style={{ display: 'flex', alignItems: 'center' }}
                  onClick={() => setImportModalVisible(true)}
                >
                  <i className="fas fa-upload me-2" /> {t('import')}
                </Button>
                <Dropdown overlay={downloadMenu} trigger={['click']}>
                  <Button type="primary" style={{ display: 'flex', alignItems: 'center' }}>
                    <Download size={16} className="me-2" /> {t('download')}
                  </Button>
                </Dropdown>
              </div>
            </div>
          </div>

          {/* Metric Cards */}
          <Row gutter={[16, 16]} className="mb-4">
            {metricCards.map((card, index) => (
              <Col key={index} xs={24} sm={12} md={8}>
                <Card style={{ borderLeft: `4px solid ${card.color}`, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                  <h3 style={{ fontSize: '1.5rem', margin: 0, color: card.color }}>{card.value}</h3>
                  <p style={{ margin: 0, color: '#666', fontFamily: "'Roboto', sans-serif" }}>{card.label}</p>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Guest List Table */}
          <div className="row g-0">
            <div className="col-md-12">
              <div className="card" style={{ borderRadius: '0', border: '1px solid #dee2e6', borderBottom: 'none' }}>
                <div className="card-header" style={{ backgroundColor: '#fff', padding: '12px 16px', borderBottom: 'none' }}>
                  <h5
                    className="mb-0 d-flex align-items-center"
                    style={{ fontSize: '14px', fontWeight: 600, color: '#333', textTransform: 'uppercase' }}
                  >
                    {t('guest_details')} (Total Guests: {pagination.total})
                  </h5>
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '0',
                      left: '0',
                      width: '100%',
                      height: '4px',
                      backgroundColor: '#007bff',
                    }}
                  />
                </div>
                <div className="card-body p-0">
                  {loading ? (
                    <div className="text-center my-5">
                      <div className="loader">
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                      </div>
                    </div>
                  ) : (
                    <Table
                      dataSource={data}
                      columns={[
                        {
                          title: t('guest_name'),
                          dataIndex: 'full_name',
                          key: 'full_name',
                          sorter: true,
                          render: (text, record) => (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleViewProfile(record);
                              }}
                              style={{ color: '#007bff' }}
                            >
                              {text}
                            </a>
                          ),
                        },
                        {
                          title: t('email'),
                          dataIndex: 'email',
                          key: 'email',
                          sorter: true,
                        },
                        {
                          title: t('phone'),
                          dataIndex: 'phone_no',
                          key: 'phone_no',
                          sorter: true,
                        },
                        {
                          title: t('nationality'),
                          dataIndex: 'nationality',
                          key: 'nationality',
                          sorter: true,
                        },
                        {
                          title: t('stay_count'),
                          key: 'stay_count',
                          render: () => 'N/A',
                          sorter: true,
                        },
                        {
                          title: t('last_stay'),
                          key: 'last_stay',
                          render: () => 'N/A',
                          sorter: true,
                        },
                        {
                          title: t('guest_type'),
                          key: 'guest_type',
                          render: (record) => (record.is_regular ? 'Regular' : 'New'),
                          sorter: true,
                        },
                      ]}
                      rowKey="guest_id"
                      pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
                        onShowSizeChange: (current, size) => handleTableChange({ current: 1, pageSize: size }),
                      }}
                      bordered
                      className="daybook-table"
                      style={{ margin: 0 }}
                      summary={() => (
                        <Table.Summary.Row style={{ backgroundColor: '#f8f9fa' }}>
                          <Table.Summary.Cell colSpan={7} style={{ fontWeight: 600, fontSize: '14px' }}>
                            {t('total')}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell style={{ fontWeight: 600, fontSize: '14px' }}>
                            {pagination.total}
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <GuestProfileModal
            visible={profileModalVisible}
            onClose={handleCloseProfileModal}
            guest={selectedGuest}
            loading={loading}
            historicalStays={historicalStays}
          />

          <GuestImportModal
            visible={importModalVisible}
            onClose={handleCloseImportModal}
            onSuccess={handleImportSuccess}
            loading={loading}
            setLoading={setLoading}
          />
        </div>
      </div>
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');

        .table th,
        .table td {
          padding: 8px 12px;
          vertical-align: middle;
          border: 1px solid #ced4da;
        }
        .daybook-table .ant-table-thead > tr > th {
          background: #ffffff;
          font-weight: 600;
          padding: 8px 16px;
          font-size: 14px;
        }
        .daybook-table .ant-table-tbody > tr > td {
          padding: 12px 18px;
          font-size: 14px;
          height: 60px;
        }
        .daybook-table .ant-table {
          border: 1px solid #dee2e6;
        }
        .daybook-table .ant-table-tbody > tr.ant-table-row:hover > td {
          background: #f5f5f5;
        }
        .loader {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .dot {
          width: 10px;
          height: 10px;
          background-color: #007bff;
          border-radius: 50%;
          margin: 0 5px;
          animation: dot-bounce 1.4s infinite ease-in-out both;
        }
        .dot:nth-child(1) {
          animation-delay: -0.32s;
        }
        .dot:nth-child(2) {
          animation-delay: -0.16s;
        }
        @keyframes dot-bounce {
          0%,
          80%,
          100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default GuestDatabase;
